import React, { useEffect, useState } from "react";
import { FaEllipsisV } from "react-icons/fa";
import { FiUpload } from "react-icons/fi";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { BannerSpace, TableItem } from "../../../types/admin-types/adminTypes";
import { toastError, toastSuccess } from "../../../utils/toast";
import useApiService from "../../../hooks/useApiService";
import {
  getThirdPartyAdPresigndeUrl,
  getThirdPartyAds,
  updateThirdPartyAdImg,
} from "../../../services/admin-service/adminService";
import Spinner from "../../../components/common/spinner/Spinner";
import { putImgWithPresignedUrl } from "../../../services/main-service/mainService";
import CustomSelect from "../../../components/main-layout-components/custom-select/CustomSelect";
import "../../../styles/css/data-table.css";

// Extract the S3 object key from a full DigitalOcean Spaces URL
const urlToKey = (url: string): string =>
  url.split(".digitaloceanspaces.com/")[1] ?? url;

function AdminThirdPartyAdsPage() {
  const { data: adSpacesList, loading, error } = useApiService({
    serviceCall: () => getThirdPartyAds(null),
    dependencies: [],
  });

  const navigate  = useNavigate();
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [imageFile,      setImageFile]      = useState<File | null>(null);
  const [imageFileName,  setImageFileName]  = useState<string>("");
  const [tableData,      setTableData]      = useState<TableItem[]>([]);
  const [isLoading,      setIsLoading]      = useState<boolean>(false);

  async function getPresignedUrl(image: File) {
    const payload = {
      content_type:    image.type,
      extension:       image.name.split(".").pop(),
      number_of_files: 1,
    };
    const presignedUrlResponse = await getThirdPartyAdPresigndeUrl(payload);
    const preSignedUrl: string = presignedUrlResponse[0].url;
    const configs = { headers: { "Content-Type": image.type, "x-amz-acl": "public-read" } };
    await putImgWithPresignedUrl(preSignedUrl, image, configs);
    return presignedUrlResponse;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    if (!selectedOption || !imageFile) {
      toastError("Choose an ad space and upload an image first");
      setIsLoading(false);
      return;
    }
    const option = adSpacesList.find((o: BannerSpace) => o.id === selectedOption);
    if (!option) { setIsLoading(false); return; }

    try {
      const presignedUrlResponse = await getPresignedUrl(imageFile);
      const newKey = presignedUrlResponse[0].key;

      // Collect all existing keys for this banner space and append the new one
      const existingKeys = tableData
        .filter((row) => row.id === selectedOption)
        .map((row) => row.key);

      await updateThirdPartyAdImg({ id: option.id, presigned_urls: [...existingKeys, newKey] });

      setTableData((prev) => [
        ...prev,
        {
          id:       option.id,
          name:     option.location,
          imageUrl: URL.createObjectURL(imageFile),
          key:      newKey,
        },
      ]);
      setSelectedOption("");
      setImageFile(null);
      setImageFileName("");
      toastSuccess("Banner image added successfully!");
    } catch (error: any) {
      toastError(error.response?.data?.message ?? "Upload failed");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDelete(id: string, key: string) {
    try {
      // Send all remaining keys for this banner space (excluding the deleted one)
      const remainingKeys = tableData
        .filter((row) => row.id === id && row.key !== key)
        .map((row) => row.key);

      await updateThirdPartyAdImg({ id, presigned_urls: remainingKeys });
      setTableData((prev) => prev.filter((row) => !(row.id === id && row.key === key)));
      toastSuccess("Banner image removed.");
    } catch (error: any) {
      toastError(error.response?.data?.message ?? "Delete failed");
    }
  }

  useEffect(() => {
    if (adSpacesList) {
      const rows: TableItem[] = [];
      adSpacesList.forEach((o: BannerSpace) => {
        o.ad_presigned_urls.forEach((url: string) => {
          rows.push({
            id:       o.id,
            name:     o.location,
            imageUrl: url,
            key:      urlToKey(url),
          });
        });
      });
      setTableData(rows);
    }
  }, [adSpacesList]);

  if (loading) return <Spinner />;
  if (error)   return <p className="text-center mt-5">{error}</p>;

  const spaceOptions = adSpacesList?.map((o: BannerSpace) => ({
    key:   o.id,
    value: `${o.location} (${o.resolution})`,
  })) ?? [];

  return (
    <div className="data-page">
      <div className="container">
        <div className="data-page__card">

          {/* Header */}
          <div className="data-page__header">
            <div className="data-page__title-group">
              <button className="data-page__back-btn" onClick={() => navigate("/admin/dashboard")}>
                <MdOutlineArrowBackIosNew size={18} />
              </button>
              <div>
                <h4 className="data-page__title">Third Party Ads</h4>
                <p className="data-page__subtitle">Manage banner ad spaces and images</p>
              </div>
            </div>
          </div>

          {/* Upload form */}
          <div style={{ padding: "1.5rem 1.75rem", borderBottom: "1px solid #f1f5f9" }}>
            <p style={{ fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#94a3b8", marginBottom: "1rem" }}>
              Add Banner Image
            </p>
            <form onSubmit={handleSubmit}>
              <div className="row g-3 align-items-end">
                <div className="col-sm-12 col-md-5">
                  <CustomSelect
                    id="adSpaceSelect"
                    label="Choose an ad space"
                    value={selectedOption}
                    options={spaceOptions}
                    onChange={setSelectedOption}
                  />
                </div>
                <div className="col-sm-12 col-md-5">
                  <label
                    htmlFor="thirdPartyImgInput"
                    className="form-page__file-btn"
                    style={{ marginBottom: 0 }}
                  >
                    <FiUpload size={15} />
                    {imageFileName || "Upload an image"}
                  </label>
                  <input
                    type="file"
                    id="thirdPartyImgInput"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      setImageFile(f);
                      setImageFileName(f ? f.name : "");
                    }}
                  />
                </div>
                <div className="col-sm-12 col-md-2">
                  <button
                    type="submit"
                    className="btn btn-danger fw-bold w-100"
                    style={{ borderRadius: "50rem", padding: "0.6rem 1rem" }}
                    disabled={isLoading}
                  >
                    {isLoading ? "Saving…" : "Add"}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Table */}
          <div className="data-table-wrap" style={{ flex: 1 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Ad Space</th>
                  <th>Image</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!tableData.length ? (
                  <tr>
                    <td colSpan={4} className="data-table-empty">
                      No banner images configured yet
                    </td>
                  </tr>
                ) : (
                  tableData.map((row, index) => (
                    <tr key={`${row.id}-${row.key}`}>
                      <th scope="row">{index + 1}</th>
                      <td style={{ fontWeight: 600, color: "#1e293b" }}>{row.name}</td>
                      <td>
                        <a
                          href={row.imageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="listing-link"
                        >
                          View image
                        </a>
                      </td>
                      <td>
                        <div className="dropdown">
                          <button
                            className="action-menu-btn"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            <FaEllipsisV size={14} />
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end">
                            <li>
                              <button
                                className="dropdown-item text-danger"
                                onClick={() => handleDelete(row.id, row.key)}
                              >
                                Delete
                              </button>
                            </li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </div>
  );
}

export default AdminThirdPartyAdsPage;
